import { useState, useEffect, useCallback } from 'react';
import { Typography, Table, Tag, Tooltip, Select, DatePicker, theme } from 'antd';
import dayjs from 'dayjs';
import { authFetch } from '../../utils/api';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const ACTION_LABELS: Record<string, { text: string; color: string }> = {
  // 基本 CRUD
  create: { text: '新增', color: 'green' },
  update: { text: '更新', color: 'blue' },
  delete: { text: '刪除', color: 'red' },
  status_change: { text: '狀態變更', color: 'orange' },
  toggle: { text: '啟停切換', color: 'purple' },
  batch_delete: { text: '批量刪除', color: 'red' },
  batch_assign: { text: '批量指派', color: 'cyan' },
  assign: { text: '指派', color: 'cyan' },
  // 連線 / 裝置
  connect: { text: '連線', color: 'green' },
  disconnect: { text: '斷線', color: 'orange' },
  kick_device: { text: '踢出裝置', color: 'volcano' },
  continue: { text: '繼續', color: 'default' },
  // 域名池
  burn: { text: '銷毀域名', color: 'red' },
  domain_cert_expiring: { text: '證書即將到期', color: 'orange' },
  domain_cert_probe_failed: { text: '證書探測失敗', color: 'red' },
  // 節點同步
  node_sync_single: { text: '單節點同步', color: 'blue' },
  nodes_sync_all: { text: '全節點同步', color: 'blue' },
  // 協議 / 健康檢查
  protocol_health_check_trigger: { text: '協議健檢觸發', color: 'geekblue' },
  protocol_down: { text: '協議故障', color: 'red' },
  health_check: { text: '健康檢查', color: 'blue' },
  health_check_all: { text: '批量健檢', color: 'blue' },
  // VLESS / 部署
  deploy: { text: '部署', color: 'green' },
  regenerate_vless_uuid: { text: '重生 VLESS UUID', color: 'magenta' },
  // 等級
  tier_downgrade: { text: '等級降級', color: 'orange' },
  tier_expired: { text: '等級到期', color: 'orange' },
  // 安全 / Attestation
  'connect-ticket': { text: '連線票據', color: 'gold' },
  'vpn-config-rotate': { text: '配置輪替', color: 'purple' },
  vpn_config_rotate_success: { text: '配置輪替成功', color: 'green' },
  vpn_config_rotate_rejected: { text: '配置輪替拒絕', color: 'red' },
  app_attest_register_success: { text: 'App Attest 註冊成功', color: 'green' },
  app_attest_register_rejected: { text: 'App Attest 註冊拒絕', color: 'red' },
  app_attest_assert_rejected: { text: 'App Attest 驗證拒絕', color: 'red' },
};

const RESOURCE_LABELS: Record<string, string> = {
  user: '用戶',
  node: '節點',
  tier: '等級',
  admin: '管理員',
  brand: '品牌',
  client: '客戶端',
  domain_pool: '域名池',
  vless_account: 'VLESS 帳號',
  node_protocol: '節點協議',
  dns_txt_signing_key: 'DNS TXT 簽章金鑰',
};

interface AuditRecord {
  id: number;
  operatorId: number;
  operatorName: string;
  action: string;
  resourceType: string;
  resourceId: number | null;
  details: string | null;
  ip: string | null;
  createdAt: string;
}

export const AuditList: React.FC = () => {
  const { token } = theme.useToken();
  const [data, setData] = useState<AuditRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [action, setAction] = useState('');
  const [resourceType, setResourceType] = useState('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      if (action) params.set('action', action);
      if (resourceType) params.set('resourceType', resourceType);
      if (dateRange) {
        params.set('startTime', dateRange[0].startOf('day').toISOString());
        params.set('endTime', dateRange[1].endOf('day').toISOString());
      }
      const res = await authFetch(`/api/admin/audit-logs?${params}`);
      const result = await res.json();
      if (result.code === 0) {
        setData(result.data.list || []);
        setTotal(result.data.total || 0);
      }
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [page, pageSize, action, resourceType, dateRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="page-enter">
      <Title level={4} style={{ marginBottom: 24 }} data-testid="audit-title">操作審計日誌</Title>

      <div className="mobile-filters" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
        <Select
          value={action || undefined}
          onChange={v => { setAction(v || ''); setPage(1); }}
          allowClear
          placeholder="操作類型"
          style={{ width: 140 }}
          options={Object.entries(ACTION_LABELS).map(([k, v]) => ({ value: k, label: v.text }))}
        />
        <Select
          value={resourceType || undefined}
          onChange={v => { setResourceType(v || ''); setPage(1); }}
          allowClear
          placeholder="資源類型"
          style={{ width: 120 }}
          options={Object.entries(RESOURCE_LABELS).map(([k, v]) => ({ value: k, label: v }))}
        />
        <RangePicker
          onChange={(dates) => {
            setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null);
            setPage(1);
          }}
        />
      </div>

      <Table
        dataSource={data}
        loading={loading}
        rowKey="id"
        data-testid="audit-table"
        scroll={{ x: 1080 }}
        tableLayout="fixed"
        pagination={{
          current: page, pageSize, total, showSizeChanger: true,
          showTotal: t => `共 ${t} 筆`,
          onChange: (p, ps) => { setPage(p); setPageSize(ps); },
        }}
        locale={{ emptyText: loading ? undefined : <div style={{ padding: '40px 0' }}><p>暫無審計記錄</p></div> }}
      >
        <Table.Column dataIndex="createdAt" title="時間" width={170}
          render={(v: string) => v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '-'} />
        <Table.Column dataIndex="operatorName" title="操作者" width={120} ellipsis />
        <Table.Column dataIndex="action" title="操作" width={170}
          render={(v: string) => {
            const info = ACTION_LABELS[v] || { text: v, color: 'default' };
            const isFallback = !ACTION_LABELS[v];
            return (
              <Tooltip title={isFallback ? v : `${info.text} (${v})`}>
                <Tag
                  color={info.color}
                  style={{
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    margin: 0,
                  }}
                >
                  {info.text}
                </Tag>
              </Tooltip>
            );
          }} />
        <Table.Column dataIndex="resourceType" title="資源" width={130} ellipsis
          render={(v: string) => {
            const label = RESOURCE_LABELS[v] || v;
            const isFallback = !RESOURCE_LABELS[v];
            return (
              <Tooltip title={isFallback ? v : `${label} (${v})`}>
                <span>{label}</span>
              </Tooltip>
            );
          }} />
        <Table.Column dataIndex="resourceId" title="資源 ID" width={90} ellipsis />
        <Table.Column dataIndex="details" title="詳情" width={280} ellipsis
          render={(v: string | null) => {
            if (!v) return '-';
            try { return JSON.stringify(JSON.parse(v)); } catch { return v; }
          }} />
        <Table.Column dataIndex="ip" title="IP" width={130} ellipsis />
      </Table>
    </div>
  );
};
